import { format } from 'date-fns';
import { Frown, Lock, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  Card,
  CardHeader,
  CardDescription,
  CardTitle,
  CardContent,
} from '@/Domain/components/Shared/Card/Card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/Domain/components/Shared/Chart/chart';
import { ChartPlaceholder } from '@/Domain/components/Shared/ChartPlaceholder';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';

type Props = {
  data: Array<Record<string, string | number>>;
  config: Record<string, { label: string; color: string }>;
  totals: Record<string, number>;
  hasTopics: boolean;
  isLoading: boolean;
};

export const TopicDistributionChart = ({ data, config, totals, hasTopics, isLoading }: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'ai_insights.topics' });
  const { isActivated, isFetched } = useSubscription();

  // Check if user has access to topics feature (Professional+ plan)
  const hasTopicsAccess = isActivated('topics');

  // Check if there's topic data for the selected date range
  const hasTopicData = data && data.length > 0 && Object.keys(config || {}).length > 0;

  const renderContent = () => {
    // Early return for subscription access check
    if (isFetched && !hasTopicsAccess) {
      return (
        <ChartPlaceholder
          icon={Lock}
          titleTranslationKey="no_topic_access.title"
          descriptionTranslationKey="no_topic_access.description"
          buttonTextTranslationKey="no_topic_access.button_text"
          buttonLink="/settings/subscription"
        />
      );
    }

    // Early return for no topics configured
    if (!hasTopics) {
      return (
        <ChartPlaceholder
          icon={Settings}
          titleTranslationKey="no_topics_assigned.title"
          descriptionTranslationKey="no_topics_assigned.description"
          buttonTextTranslationKey="no_topics_assigned.button_text"
          buttonLink="/insights/insights?tab=manageTopics"
          spacing="large"
        />
      );
    }

    // Early return for no data in range
    if (!hasTopicData) {
      return (
        <ChartPlaceholder
          icon={Frown}
          titleTranslationKey="no_data_in_range.title"
          descriptionTranslationKey="no_data_in_range.description"
          spacing="small"
        />
      );
    }

    // Render the actual chart when all conditions are met
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6 items-stretch">
        <ChartContainer
          fluidHeight
          config={config}
          className={`h-full min-h-[14rem] w-full ${isLoading ? 'animate-pulse' : ''}`}
        >
          <LineChart accessibilityLayer data={data} margin={{ top: 4, left: 0, right: 4 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => format(new Date(value), 'dd MMM')}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              allowDecimals={false}
              width={36}
            />
            <ChartTooltip
              cursor={false}
              wrapperStyle={{ whiteSpace: 'normal' }}
              labelFormatter={(value) => format(new Date(value), 'dd MMM yyyy')}
              content={<ChartTooltipContent indicator="dot" className="w-auto" />}
            />
            {Object.keys(config || {}).map((key) => (
              <Line
                key={key}
                dataKey={key}
                type="monotone"
                stroke={`var(--color-${key})`}
                strokeWidth={2}
                dot={false}
              />
            ))}
          </LineChart>
        </ChartContainer>
        <div className="pl-2">
          <ul className="flex flex-col gap-2 w-full max-w-[280px]">
            {Object.keys(config || {}).map((key) => (
              <li key={key} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-2 w-2 rounded-[2px] shrink-0"
                    style={{ backgroundColor: (config as any)[key]?.color as string }}
                  />
                  <span className="truncate text-xs text-neutral-600">
                    {(config as any)[key]?.label}
                  </span>
                </div>
                <span className="font-mono font-medium tabular-nums text-xs text-neutral-950">
                  {Number((totals as any)[key] || 0).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <Card className="xl:col-span-2">
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('distribution_over_time')}</CardDescription>
      </CardHeader>
      <CardContent>{renderContent()}</CardContent>
    </Card>
  );
};
